import { IncomeInputStoreService } from '@hb/modules/applicants/income-input/income-input-store.service';
import { ButtonKey, QuestionKey } from '@hb/modules/global/global.constants';
import { AmountFormControl } from '@hb/domain';
import { AnalyticsClickDirective } from '@hb/directives';
import { AdditionalInfoComponent, NextButtonComponent } from '@hb/modules';
import { mainContentKeyMap, partnerContentKeyMap } from '@hb/modules/applicants/applicants.constants';
import { AnalyticsService } from '@hb/services/analytics/analytics.service';
import { MortgageCalculationService } from '@hb/services/mortgage-calculation/mortgage-calculation.service';

import { Component, inject, OnInit } from '@angular/core';
import { FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';

import { ApContentModule } from '@ap/content';
import { ApIncomeComponent } from '@ap/income';
import { ApActionIconComponent, ApIconComponent } from '@ap/icon';
import { ApIconButtonLinkComponent } from '@ap/icon-link';

@Component({
    selector: 'income-input',
    templateUrl: './income-input.component.html',
    styleUrls: ['./income-input.component.scss'],
    standalone: true,
    imports: [
        ApContentModule,
        ReactiveFormsModule,
        AnalyticsClickDirective,
        ApIncomeComponent,
        ApIconComponent,
        ApActionIconComponent,
        ApIconButtonLinkComponent,
        AdditionalInfoComponent,
        NextButtonComponent
    ]
})
export class IncomeInputComponent implements OnInit {
    protected readonly analyticsService = inject(AnalyticsService);
    protected readonly store = inject(IncomeInputStoreService);
    protected readonly mortgageCalculationService = inject(MortgageCalculationService);

    protected readonly partnerContentKeyMap = partnerContentKeyMap;
    protected readonly contentKeyMap = mainContentKeyMap;
    mainApplicantAnnualIncomeForm: FormGroup;
    coApplicantAnnualIncomeForm: FormGroup;
    questionKey = QuestionKey.INCOME;
    coQuestionKey = QuestionKey.INCOME_CO_APPLICANT;

    hasCoApplicant = false;
    showCoApplicant = false;

    ngOnInit(): void {
        this.store.getHasCoApplicant().subscribe((hasCoApplicant) => {
            this.hasCoApplicant = hasCoApplicant;
        });
        this.store.getFormFieldValidationObservable().subscribe(() => this.onClick());

        this.showCoApplicant = this.hasCoApplicant;
        this.createMainApplicantIncomeForms();
        this.createCoApplicantIncomeForms();

        this.getFormControl(this.mainApplicantAnnualIncomeForm)
            ?.onUpdate()
            .subscribe((amount) => this.store.setMainApplicantIncome(amount));
        this.getFormControl(this.coApplicantAnnualIncomeForm)
            ?.onUpdate()
            .subscribe((amount) => this.store.setCoApplicantIncome(amount));
    }

    protected setShowCoApplicant(event: boolean): void {
        this.showCoApplicant = event;
        if (!event) {
            this.getFormControl(this.coApplicantAnnualIncomeForm)?.removeValidators(Validators.required);
            this.getFormControl(this.coApplicantAnnualIncomeForm).resetControl(this.coQuestionKey);
        }
    }

    showButton(): boolean {
        return (
            !this.store.isSummary &&
            (this.mortgageCalculationService.showDesktopDisplay() || this.mortgageCalculationService.showStickyMobileDisplay())
        );
    }

    createMainApplicantIncomeForms(): void {
        this.mainApplicantAnnualIncomeForm = new FormGroup({
            yearlyIncome: new AmountFormControl(
                this.analyticsService,
                this.store.getMainApplicantIncome()?.amount.toString(),
                this.questionKey
            )
        });
    }

    createCoApplicantIncomeForms(): void {
        this.coApplicantAnnualIncomeForm = new FormGroup({
            yearlyIncome: new AmountFormControl(
                this.analyticsService,
                this.store.getCoApplicantIncome()?.amount.toString(),
                this.coQuestionKey
            )
        });
    }

    onClick(): void {
        if (this.isValidController()) {
            this.store.nextQuestion();
        } else {
            this.controllerVerify();
        }
    }

    private isValidController(): boolean {
        return this.showCoApplicant
            ? this.getFormControl(this.mainApplicantAnnualIncomeForm).valid && this.getFormControl(this.coApplicantAnnualIncomeForm).valid
            : this.getFormControl(this.mainApplicantAnnualIncomeForm).valid;
    }

    private controllerVerify(): void {
        const mainApplicantControl = this.getFormControl(this.mainApplicantAnnualIncomeForm);
        mainApplicantControl.markAsDirty();
        mainApplicantControl.markAsTouched();
        mainApplicantControl.updateValueAndValidity();
        if (this.showCoApplicant) {
            const coApplicantControl = this.getFormControl(this.coApplicantAnnualIncomeForm);
            coApplicantControl.markAsDirty();
            coApplicantControl.markAsTouched();
            coApplicantControl.updateValueAndValidity();
        }
    }

    private getFormControl(form: FormGroup): AmountFormControl<number> {
        return form?.controls['yearlyIncome'] as AmountFormControl<any>;
    }

    sendAnalyticsCalculateIncome() {
        this.analyticsService.sendClickUpdate('income-calculator', ButtonKey.INCOME_CALCULATOR);
    }
}
